import _ from "lodash";
import React from "react";
import {NullAsDefault} from 'views/Shared'
import {ItemInformation} from 'components/common/ItemInformation'
import {CatholicHouseholdActionTypes} from "../../../AppLocals/CatholicHousehold/consts/CatholicHouseholdActionTypes";
import {BsCheckCircle, BsClock, BsGeoAlt, BsHouseFill, BsPeopleFill, BsXCircle} from "react-icons/bs";
import {renderIf} from "../../../Shared";
import classNames from "classnames";
import {formatDate} from "../../../../helpers/datetimeHelpers";
import {FiChevronsRight} from "react-icons/fi";

export const CatholicHouseholdStatus = {
  pending: {
    value: -1,
    label: 'pending',
    icon: <BsClock size={10} />,
    textClass: 'text-yellow-600',
    badgeClass: 'text-yellow-700 bg-yellow-50 border border-yellow-200',
  },
  approved: {
    value: 1,
    label: 'approved',
    icon: <BsCheckCircle size={10} />,
    textClass: 'text-green-600',
    badgeClass: 'text-green-700 bg-green-50 border border-green-200',
  },
  rejected: {
    value: -2,
    label: 'rejected',
    icon: <BsXCircle size={10} />,
    textClass: 'text-red-600',
    badgeClass: 'text-red-700 bg-red-50 border border-red-200',
  },
}

export const renderCatholicHousehold =
  ({translate: t, isLg, actions, renderContextMenu = NullAsDefault, handleAction = NullAsDefault} = {}) =>
  (item, index) => {
    const {title, source_idcode, eff_date, status, total, parish, parish_name} = item ?? {}
    const current_status = _.find(CatholicHouseholdStatus, {value: status}) ?? {}
    const parish_display = parish?.name || parish_name
    const hasContextMenu = !!item?.edit && _.isFunction(renderContextMenu)

    const handleClick = () => {
      if (status !== 1) {
        handleAction(CatholicHouseholdActionTypes.CATHOLIC_HOUSEHOLD_APPROVE, item)
      } else {
        handleAction(CatholicHouseholdActionTypes.CATHOLIC_HOUSEHOLD_VIEW, item)
      }
    }

    return (
      <div
        key={index}
        className="p-3 md:p-4 flex gap-3 custom-item-render list-item">
        {/* Icon container */}
        <div className="flex-none mt-0.5">
          <div
            className="w-11 h-11 md:w-14 md:h-14 rounded-xl flex items-center justify-center"
            style={{background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)'}}>
            <BsHouseFill
              size={isLg ? 22 : 18}
              className="text-indigo-500"
            />
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col flex-1 min-w-0 gap-1">
          {/* Title row: title + status badge + context menu (inline, no absolute) */}
          <div className="flex items-start gap-1.5">
            <div
              onClick={handleClick}
              className="flex-1 font-bold text-base text-color-000 hover:text-primary tracking-wide cursor-pointer leading-snug min-w-0">
              {`${t('catholic household')} ${title}`}
            </div>
            {current_status?.label && (
              <span
                className={classNames(
                  'flex-none flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-medium mt-0.5',
                  current_status.badgeClass
                )}>
                {current_status.icon}
                {t(current_status.label)}
              </span>
            )}
            {/* Context menu sits inline — no absolute/overlap */}
            {hasContextMenu && (
              <div className="flex-none contextIcon">
                {actions}
                {renderContextMenu(item)}
              </div>
            )}
          </div>

          {/* Parish */}
          {renderIf(
            parish_display,
            <div className="flex items-center gap-1 text-xs text-color-400">
              <BsGeoAlt size={11} className="flex-none" />
              <span className="truncate">{parish_display}</span>
            </div>
          )}

          {/* Meta info row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
            {renderIf(
              source_idcode,
              <ItemInformation
                showIcon={true}
                label="number of households"
                value={source_idcode}
                className="text-xs italic tracking-wider"
              />
            )}
            {renderIf(
              eff_date,
              <ItemInformation
                showIcon={true}
                label="date of immigration"
                value={formatDate(eff_date, 'DD/MM/YYYY')}
                className="text-xs italic tracking-wider"
              />
            )}
            <div className="flex items-center gap-1">
              <BsPeopleFill size={11} className="text-color-400" />
              <span className="text-xs text-color-300 font-medium">
                {total?.members > 0 ? `${total.members} ${t('members')}` : t('no members')}
              </span>
            </div>
          </div>

          {/* View detail link for approved items */}
          {renderIf(
            status === 1,
            <div className="flex justify-end mt-1">
              <span
                onClick={() => handleAction(CatholicHouseholdActionTypes.CATHOLIC_HOUSEHOLD_MEMBER, item)}
                className="flex items-center gap-1 text-xs text-color-300 hover:text-secondary-600 tracking-wide cursor-pointer">
                {t('view detail')} <FiChevronsRight size={13} />
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }
